import React from 'react';
import { Chip, IconButton } from '@mui/material';
import {
  capitalize, copyToClipboard, getLanColor, isEmptyObject, locations,
} from 'utils';
import assets from 'themes/base/assets';
import { useNotification } from 'screens/shared/hooks/notifications';
import Icon from './icon';

const Separator = ({ style = {} }) => <span style={{ color: 'gray', margin: '0 6px', ...style }}>-</span>;

const StatusLED = ({ state = 'UNKNOWN', size = 12, className = '' }) => (
  <span
    style={{
      borderRadius: '50%', width: size, height: size, display: 'inline-block',
    }}
    className={`status-background-UNKNOWN status-background-${state?.toUpperCase?.()} ${className}`}
    title={capitalize(state)}
  />
);

const StatusText = ({ state, className = '' }) => (
  <span className={`status-color-${state} ${className}`} style={{ whiteSpace: 'nowrap' }}>
    {capitalize(state)}
  </span>
);

const Status = ({ state, className = '' }) => (
  <span className={className}>
    <StatusLED state={state} className="relative top-1" />
    <span className={`m-l-5 status-color-${state}`}>{capitalize(state)}</span>
  </span>
);

const Location = ({ location, className = '' }) => {
  const { image = '', name = '' } = locations[location] || {};

  return (
    <span className={className}>
      {!!image && <img src={image} alt="Country flage" style={{ width: 12 }} />}
      {!image && <Icon name="globe-europe" />}
      {!!name && <span className="m-l-5">{`${name} (${location})`}</span>}
      {!name && <span className="m-l-5">{location}</span>}
    </span>
  );
};

const IPs = ({ ips, className = '', itemClassName }) => (
  <div className={`m-t-4 m-b-4 ${className}`}>
    {(ips || []).map((e, i) => (
      <Chip key={i} label={<span>{e}</span>} className={`h-26 background-ground m-r-4 m-b-4 ${itemClassName}`} />
    ))}
  </div>
);

const LAN = ({ id, name, className }) => (
  <span className={`flex flex-align-center ${className}`}>
    <Icon name="ethernet p-5 b-r-4 color-white font-16 m-r-5" style={{ backgroundColor: getLanColor(id) }} />
    {`LAN ${id} ${name ? ` - ${name}` : ''}`}
  </span>
);

const LANBasic = ({ lanId }) => (
  <span style={{ backgroundColor: getLanColor(lanId) }} className="lan">{lanId}</span>
);

export const IconLabel = ({
  icon, label, color = 'base', size = 15, colorsMap, iconsMap, isColorLabel = false, isCapitalize = true, className = '',
}) => {
  color = isEmptyObject(colorsMap) ? color : colorsMap[label] || color;
  icon = isEmptyObject(iconsMap) ? icon : iconsMap[label] || icon;
  return (
    <span className={className}>
      {icon && <Icon name={icon} className={`font-${size} color-${color}`} />}
      <span className={`font-${size} ${isColorLabel ? `color-${color}` : ''} ${icon ? 'm-l-5' : ''}`}>
        {isCapitalize ? capitalize(label) : label}
      </span>
    </span>
  );
};

const NoData = ({ msg = 'No Data' }) => (
  <div
    className="empty"
    style={{
      marginTop: 15,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <img src={assets.EMPTY} alt="Empty" style={{ width: 80, opacity: 0.8 }} />
    <h3 className="-m-t-10" style={{ color: 'lightgray', fontSize: 'var(--font-size-highlighted-text)' }}>{msg}</h3>
  </div>
);

const DetailsLine = ({ object, isBorder = false }) => (
  <span>
    {Object.keys(object).map((key) => (
      <span key={key}>
        <span>{key}</span>
        <span className="m-l-5 m-r-15 p-b-2 color-gray" style={isBorder ? { borderBottom: '1px dashed #bbbbbb' } : {}}>{object[key]}</span>
      </span>
    ))}
  </span>
);

const Code = ({ code, className = '' }) => (
  <pre
    style={{
      background: '#F3F6F9',
      borderRadius: '4px',
      padding: '10px',
      margin: '10px 0',
      overflow: 'auto',
      color: '#2D3843',
      border: '1px solid #CDD2D7',
    }}
    className={className}
  >
    {code}
  </pre>
);

const CopyButton = ({ copyText, notificationMessage }) => {
  const [,, setNotification] = useNotification({});

  return (
    <IconButton
      onClick={() => {
        copyToClipboard(copyText);
        setNotification({
          type: 'success',
          message: notificationMessage || 'Text has been copied!',
        });
      }}
      size="small"
    >
      <Icon
        name="copy"
        style={{
          float: 'right',
          color: 'grey',
          cursor: 'pointer',
        }}
      />
    </IconButton>
  );
};

export default {
  Separator,
  Status,
  StatusLED,
  StatusText,
  Location,
  IPs,
  LAN,
  LANBasic,
  IconLabel,
  NoData,
  DetailsLine,
  Code,
  CopyButton,
};
